import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, fmtN, fmtDateTime } from "@/components/primitives";
import { HealthPill, VerificationPanel } from "@/pages/Connection";
import EmptyStateCard from "@/components/EmptyStateCard";

function PageHeader({ title, sub, right }) {
  return (
    <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
      <div>
        <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>{title}</div>
        {sub && <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>{sub}</div>}
      </div>
      <div className="flex" style={{ gap: 12 }}>{right}</div>
    </div>
  );
}

function StatusCard({ label, value, valueColor, sub }) {
  return (
    <Card style={{ flex: 1, padding: "20px 22px" }}>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.8px",
        color: valueColor || "var(--ui-text-hi)", marginTop: 8 }}>{value}</div>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 8 }}>{sub}</div>
    </Card>
  );
}

function CatalogueEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["catalog"], queryFn: () => endpoints.catalog(false) });
  const [drafts, setDrafts] = React.useState({});
  const items = data?.items || [];
  const cats = data?.categories || [];
  const inp = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 8, padding: "6px 10px", color: "var(--ui-text-hi)", fontFamily: "'Inter',sans-serif", fontSize: 13 };
  const save = async (it) => {
    const d = drafts[it.id] || {};
    await endpoints.saveCatalog(it.id, {
      category: d.category !== undefined ? (d.category || null) : it.category,
      unitKg: d.unitKg !== undefined ? (d.unitKg === "" ? null : parseFloat(d.unitKg)) : it.unitKg,
    });
    setDrafts((p) => { const n = { ...p }; delete n[it.id]; return n; });
    await qc.invalidateQueries();
  };
  const setD = (id, k, v) => setDrafts((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  return (
    <Card style={{ marginTop: 20, padding: 26 }} data-testid="catalogue-editor">
      <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Catalogue categories &amp; units</div>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 14 }}>
        category &amp; kg-per-unit are editable · a manual edit overrides the derived rule · uncategorised items are excluded from volume
      </div>
      <div style={{ maxHeight: 340, overflowY: "auto" }}>
        {items.map((it, i) => {
          const d = drafts[it.id] || {};
          const catVal = d.category !== undefined ? d.category : (it.category || "");
          const unitVal = d.unitKg !== undefined ? d.unitKg : (it.unitKg ?? "");
          const dirty = d.category !== undefined || d.unitKg !== undefined;
          const uncat = !it.category;
          return (
            <div key={it.id} data-testid={`catalog-row-${it.partNo}`} className="flex items-center" style={{ gap: 12, padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
              <span className="num" style={{ fontSize: 12.5, color: uncat ? "var(--sv-watch)" : "var(--ui-text-hi)", width: 120 }}>{it.partNo}</span>
              <span className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", flex: 1 }}>{it.name}</span>
              <select data-testid={`catalog-category-${it.partNo}`} value={catVal} onChange={(e) => setD(it.id, "category", e.target.value)} style={inp}>
                <option value="">uncategorised</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid={`catalog-unitkg-${it.partNo}`} type="number" placeholder="kg/unit" value={unitVal} onChange={(e) => setD(it.id, "unitKg", e.target.value)} style={{ ...inp, width: 90 }} />
              <span className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)", width: 60, textTransform: "uppercase" }}>{it.categorySource}</span>
              <Button variant="secondary" data-testid={`catalog-save-${it.partNo}`} onClick={() => save(it)} disabled={!dirty} style={{ padding: "6px 12px", fontSize: 12 }}>Save</Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SiteHealth({ sh }) {
  const nav = useNavigate();
  if (!sh) return null;
  const dm = sh.dryMachine || {};
  const snr = sh.siteNotRecorded || {};
  const Fig = ({ label, value, color }) => (
    <div>
      <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 22, fontWeight: 700, color: color || "var(--ui-text-hi)", marginTop: 3 }}>{value}</div>
    </div>
  );
  return (
    <Card style={{ marginTop: 20, padding: 26 }} data-testid="site-health">
      <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Site-level detection</div>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 18 }}>
        detection keys on the site, not the customer — a site ordering quarterly cannot be baselined, and that is correct, not a fault
      </div>

      <div className="flex" style={{ gap: 34, flexWrap: "wrap", marginBottom: 16 }}>
        <Fig label="Sites tracked" value={fmtN(sh.totalSites)} />
        <Fig label="Baselineable" value={fmtN(sh.baselineable)} color="var(--sv-good)" />
        <Fig label="Insufficient history" value={fmtN(sh.insufficientHistory)} color="var(--sv-watch)" />
        <Fig label="No data" value={fmtN(sh.noData)} />
        <Fig label="Assessed at company level" value={fmtN(sh.companyFallbacks)} color="var(--ac-indigo-hi)" />
      </div>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginBottom: 8, lineHeight: 1.5 }}>
        {fmtN(sh.companyFallbacks)} companies are assessed at company level — their sites order too infrequently to baseline individually, so the company carries one assessment rather than fabricating a per-site one.
      </div>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginBottom: 22, lineHeight: 1.5 }}>
        “No data” ({fmtN(sh.noData)}) splits into {fmtN(sh.noDataWithMachine)} sites with a machine but no bean orders in the synced window (the dry-machine list below) and {fmtN(sh.noDataEmpty)} empty site records with no machine and no orders — dormant, out of scope.
        {sh.internalAccountsExcluded > 0 && ` ${fmtN(sh.internalAccountsExcluded)} internal accounts are excluded from these figures and the working lists.`}
      </div>

      {/* Dry machines — the two working lists, directly clickable */}
      <div style={{ borderTop: "1px solid var(--ui-line-soft)", paddingTop: 18 }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>Dry machines — placed, no beans</div>
          <span className="num" style={{ fontSize: 14, fontWeight: 700, color: "var(--sv-crit)" }}>{fmtN(dm.total)}</span>
        </div>
        <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 4, marginBottom: 6 }}>
          a machine is on site but no beans have been ordered in the synced window
        </div>
        <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginBottom: 14, lineHeight: 1.5 }}>
          Order history: <b style={{ color: "var(--ui-text-hi)" }}>{fmtN(dm.stopped)}</b> stopped · <b style={{ color: "var(--ui-text-hi)" }}>{fmtN(dm.neverOrdered)}</b> never ordered (machine placed after {sh.syncBoundary}) · <b style={{ color: "var(--sv-watch)" }}>{fmtN(dm.unknown)}</b> unknown — machine predates our history, so absence of orders proves nothing. A 24–36 month backfill would move most of the {fmtN(dm.unknown)} into “never” or “stopped”.
        </div>
        <div className="flex" style={{ gap: 14, flexWrap: "wrap" }}>
          <button data-testid="dry-debt-link" onClick={() => nav("/sites?filter=dry&ownership=debt")}
            style={{ flex: "1 1 240px", textAlign: "left", cursor: "pointer", background: "var(--sv-crit-bg)", border: "1px solid var(--sv-crit)", borderRadius: 12, padding: "16px 18px" }}>
            <div className="num" style={{ fontSize: 26, fontWeight: 700, color: "var(--sv-crit)" }}>{fmtN(dm.debt)}</div>
            <div className="prose-i" style={{ fontSize: 13, fontWeight: 600, color: "var(--ui-text-hi)", marginTop: 4 }}>Contracted &middot; a debt conversation →</div>
            <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>these owe C4B volume under contract</div>
          </button>
          <button data-testid="dry-winback-link" onClick={() => nav("/sites?filter=dry&ownership=winback")}
            style={{ flex: "1 1 240px", textAlign: "left", cursor: "pointer", background: "var(--ac-indigo-soft)", border: "1px solid var(--ac-indigo)", borderRadius: 12, padding: "16px 18px" }}>
            <div className="num" style={{ fontSize: 26, fontWeight: 700, color: "var(--ac-indigo-hi)" }}>{fmtN(dm.winBack)}</div>
            <div className="prose-i" style={{ fontSize: 13, fontWeight: 600, color: "var(--ui-text-hi)", marginTop: 4 }}>Owned / unknown &middot; a win-back list →</div>
            <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>no contractual debt — a sales opportunity</div>
          </button>
        </div>
      </div>

      {/* Site-not-recorded bucket — must exist even at zero (§1.2) */}
      <div style={{ borderTop: "1px solid var(--ui-line-soft)", paddingTop: 18, marginTop: 18 }}>
        <div className="flex items-center" style={{ justifyContent: "space-between" }}>
          <div>
            <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>Site not recorded</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, maxWidth: 520 }}>
              bean volume that resolves to a customer but not to a site — it still counts at customer level and never vanishes. Empty today; it will populate the moment C4B adds or relocates a site.
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="num" style={{ fontSize: 22, fontWeight: 700, color: snr.kg > 0 ? "var(--sv-watch)" : "var(--sv-good)" }}>{fmtN(snr.kg)} kg</div>
            <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}>{fmtN(snr.rows)} lines</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function DataHealth() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const { data } = useQuery({ queryKey: ["health"], queryFn: endpoints.health });
  const { data: log } = useQuery({ queryKey: ["synclog"], queryFn: endpoints.syncLog, enabled: showLog });

  const runSync = async () => {
    setBusy(true);
    try { await endpoints.syncNow(); await qc.invalidateQueries(); } finally { setBusy(false); }
  };

  const sevColor = (s) => ({ crit: "var(--sv-crit)", risk: "var(--sv-risk)", watch: "var(--sv-watch)", good: "var(--sv-good)" }[s] || "var(--sv-watch)");

  const ch = data?.connectionHealth || {};
  const boundary = ch.earliestSyncedDate;
  const scopeLabel = { "90d": "last 90 days", "12m": "last 12 months", full: "full history" }[ch.syncScope];
  const noData = data && (data.records || []).every((r) => (r.count || 0) === 0);

  return (
    <div>
      <PageHeader title="Data Health"
        sub="Read-only sync from Simpro AU · every figure above traces to a source record"
        right={<>
          <HealthPill state={data?.connectionHealth?.state} />
          <Button variant="secondary" data-testid="view-sync-log" onClick={() => setShowLog((v) => !v)}>View sync log</Button>
          <Button data-testid="run-sync" onClick={runSync} disabled={busy}>{busy ? "Syncing…" : "Run sync now"}</Button>
        </>} />

      {noData && (
        <EmptyStateCard
          testid="datahealth-empty"
          style={{ marginBottom: 20 }}
          message="No data yet — run a sync to pull your Simpro history. Once a sync completes, record counts and data-quality checks appear below."
        />
      )}

      {data?.historyBoundary && data.historyBoundary.suppressed && data.historyBoundary.suppressed.length > 0 && (
        <div data-testid="history-boundary" style={{ marginBottom: 20, background: "var(--sv-watch-bg)", border: "1px solid var(--sv-watch)", borderRadius: 12, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
          <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--sv-watch)" }} />
          <div className="prose-i" style={{ fontSize: 13, fontWeight: 600, color: "var(--ui-text-hi)", paddingLeft: 8 }}>History boundary</div>
          <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", marginTop: 6, paddingLeft: 8, lineHeight: 1.5 }}>
            {data.historyBoundary.text}
          </div>
        </div>
      )}

      {data?.latestRun && (data.latestRun.stalled || data.latestRun.status === "error") && (
        <div data-testid="datahealth-sync-banner" style={{ marginBottom: 20, background: "var(--sv-crit-bg)", border: "1px solid var(--sv-crit)", borderRadius: 12, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
          <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--sv-crit)" }} />
          <div className="prose-i" style={{ fontSize: 13, fontWeight: 600, color: "var(--ui-text-hi)", paddingLeft: 8 }}>
            {data.latestRun.stalled ? "Last sync stalled" : "Last sync failed"}
          </div>
          <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", marginTop: 6, paddingLeft: 8 }}>
            {data.latestRun.error || "The sync did not complete."} Open the Connection screen to clear the stuck run and start a new sync.
          </div>
        </div>
      )}

      {boundary && (
        <div data-testid="history-boundary" style={{ background: "var(--ui-card-2)", border: "1px solid var(--ui-line)", borderLeft: "3px solid var(--ac-indigo)", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
          <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-hi)" }}>
            History from {fmtDateTime(boundary).split(",")[0]}{scopeLabel ? ` (${scopeLabel})` : ""} — earlier invoices not yet synced.
          </span>
        </div>
      )}

      <div className="flex" style={{ gap: 20, marginBottom: 20 }}>
        <StatusCard label="Connection" value={data?.connection?.label || "—"}
          valueColor={data?.connection?.ok ? "var(--sv-good)" : "var(--sv-crit)"} sub={data?.connection?.sub} />
        <StatusCard label="Last sync" value={data?.lastSync?.clock || "—"} sub={`duration ${data?.lastSync?.duration || ""}`} />
        <StatusCard label="API calls used" value={fmtN(data?.apiCalls?.used)} sub={data?.apiCalls?.sub} />
        <StatusCard label="Write operations" value={String(data?.writeOps?.count ?? 0)} valueColor="var(--sv-good)" sub={data?.writeOps?.sub} />
      </div>

      <div className="flex" style={{ gap: 20, alignItems: "flex-start" }}>
        {/* records synced */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Records synced</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 10 }}>last run · Simpro AU company</div>
          {(data?.records || []).map((r, i) => (
            <div key={r.name} className="flex items-center" style={{ padding: "17px 0",
              borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: "var(--sv-good)", marginRight: 12 }} />
              <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{r.name}</span>
              <span className="num" style={{ marginLeft: "auto", fontSize: 16, fontWeight: 700, color: "var(--ui-text-hi)" }}>{fmtN(r.count)}</span>
              <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginLeft: 12, width: 44 }}>synced</span>
            </div>
          ))}
        </Card>

        {/* needs attention */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Needs attention</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 14 }}>
            {(data?.needsAttention || []).length} items — these limit accuracy, not availability
          </div>
          {(data?.needsAttention || []).map((it, i) => (
            <div key={i} style={{ background: "var(--ui-card-2)", border: `1px solid ${sevColor(it.severity)}`,
              borderRadius: 12, padding: "14px 16px", marginBottom: 12, position: "relative", overflow: "hidden" }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: sevColor(it.severity) }} />
              <div className="flex items-center" style={{ justifyContent: "space-between" }}>
                <span className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)", paddingLeft: 8 }}>{it.title}</span>
                {it.action && <Button variant="secondary" style={{ padding: "6px 12px", fontSize: 12 }}>{it.action}</Button>}
              </div>
              <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", marginTop: 8, paddingLeft: 8 }}>{it.detail}</div>
            </div>
          ))}
          <div style={{ background: "var(--sv-good-bg)", border: "1px solid var(--sv-good)", borderRadius: 12,
            padding: "14px 16px", position: "relative" }}>
            <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--sv-good)" }} />
            <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-hi)", paddingLeft: 8 }}>{data?.reassurance}</div>
          </div>
        </Card>
      </div>

      <SiteHealth sh={data?.siteHealth} />

      {/* Commercial layer coverage (Amendment 2 §5e) */}
      <Card style={{ marginTop: 20, padding: 26 }}>
        <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Commercial layer coverage</div>
        <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 16 }}>
          committed volumes &amp; ownership are held in this app, not synced from Simpro
        </div>
        <div className="flex" style={{ gap: 40 }}>
          <div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>Committed volume set</div>
            <div className="num" style={{ fontSize: 24, fontWeight: 700, color: "var(--sv-good)", marginTop: 6 }}>{fmtN(data?.commitment?.set)}
              <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", fontWeight: 400 }}> set · {fmtN(data?.commitment?.notSet)} not set</span>
            </div>
          </div>
          <div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>Ownership source</div>
            <div className="flex" style={{ gap: 20, marginTop: 6 }}>
              {[["Manual", data?.ownershipSplit?.manual, "var(--ac-indigo)"],
                ["Derived", data?.ownershipSplit?.derived, "var(--ui-text-mid)"],
                ["Unknown", data?.ownershipSplit?.unknown, "var(--sv-watch)"]].map(([l, v, col]) => (
                <div key={l} className="flex items-center" style={{ gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 9999, background: col }} />
                  <span className="num" style={{ fontSize: 18, fontWeight: 700, color: "var(--ui-text-hi)" }}>{fmtN(v)}</span>
                  <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <CatalogueEditor />

      {/* Consumable structure verification (check 4) — persisted result */}
      {ch.lastVerification && (
        <Card style={{ marginTop: 20, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Consumable structure (verification)</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3 }}>
            the single most important fact about data quality — whether beans are catalogue items with part numbers &amp; quantities
          </div>
          <VerificationPanel v={ch.lastVerification} />
        </Card>
      )}

      {showLog && (
        <Card style={{ marginTop: 20, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)", marginBottom: 12 }}>Sync log</div>
          {(log?.runs || []).map((r, i) => (
            <div key={i} className="flex items-center" style={{ padding: "12px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)", gap: 16 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: r.status === "success" ? "var(--sv-good)" : r.status === "cancelled" ? "var(--sv-watch)" : r.status === "running" ? "var(--ac-indigo)" : "var(--sv-crit)" }} />
              <span className="num" style={{ fontSize: 13, color: "var(--ui-text-mid)" }}>{fmtDateTime(r.startedAt)}</span>
              <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)" }}>{r.status} · {fmtN(r.recordCount)} records · {fmtN(r.apiCalls)} API calls{r.scope ? ` · ${r.scope}` : ""}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
